import React, { useCallback, useEffect, useRef, useState } from 'react';
import './BottomSheetSelect.css';
import { ReactSVG } from 'react-svg';

const DRAG_CLOSE_THRESHOLD = 80;

const BottomSheetSelect = ({ isOpen, onClose, options, onChange, selectedValue, title, children }) => {
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const dragStartYRef = useRef(null);
    const dragOffsetRef = useRef(0);

    const safeClose = useCallback(() => {
        if (typeof onClose === 'function') {
            onClose();
        }
    }, [onClose]);

    const resetDrag = useCallback(() => {
        dragStartYRef.current = null;
        dragOffsetRef.current = 0;
        setDragOffset(0);
        setIsDragging(false);
    }, []);

    const getClientY = (event) => {
        if (event.touches && event.touches.length > 0) {
            return event.touches[0].clientY;
        }

        if (event.changedTouches && event.changedTouches.length > 0) {
            return event.changedTouches[0].clientY;
        }

        return event.clientY;
    };

    const handleDragStart = (event) => {
        if (event.type === 'mousedown' && event.button !== 0) {
            return;
        }

        if (event.cancelable) {
            event.preventDefault();
        }

        event.stopPropagation();

        const clientY = getClientY(event);

        if (typeof clientY !== 'number') {
            return;
        }

        dragStartYRef.current = clientY;
        dragOffsetRef.current = 0;
        setDragOffset(0);
        setIsDragging(true);
        setHasInteracted(true);
    };

    useEffect(() => {
        if (!isDragging) {
            return undefined;
        }

        const handleMove = (event) => {
            if (dragStartYRef.current === null) {
                return;
            }

            const clientY = getClientY(event);
            const offset = Math.max(0, clientY - dragStartYRef.current);

            dragOffsetRef.current = offset;
            setDragOffset(offset);

            if (offset > 0 && event.cancelable) {
                event.preventDefault();
            }
        };

        const handleEnd = () => {
            const shouldClose = dragOffsetRef.current > DRAG_CLOSE_THRESHOLD;

            resetDrag();

            if (shouldClose) {
                safeClose();
            }
        };

        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleEnd);
        window.addEventListener('touchcancel', handleEnd);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleEnd);

        return () => {
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
            window.removeEventListener('touchcancel', handleEnd);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
        };
    }, [isDragging, resetDrag, safeClose]);

    useEffect(() => {
        if (!isOpen) {
            resetDrag();
            setHasInteracted(false);
        }
    }, [isOpen, resetDrag]);

    const handleOverlayClick = () => {
        if (isDragging) {
            return;
        }

        safeClose();
    };

    const handleChange = (value) => {
        if (typeof onChange === 'function') {
            onChange({ target: { value } });
        } else {
            console.warn('BottomSheetSelect: onChange prop is not a function', { onChange });
        }

        safeClose();
    };

    const sheetClassName = `bottom-sheet-content${isDragging ? ' is-dragging' : ''}`;
    const sheetStyle = hasInteracted
        ? {
            transform: `translateY(${dragOffset}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out'
        }
        : undefined;

    if (!isOpen) {
        return null;
    }

    return (
        <div className="bottom-sheet-overlay" onClick={handleOverlayClick}>
            <div
                className={sheetClassName}
                style={sheetStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="bottom-sheet-drag-handle"
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                />
                <div className="bottom-sheet-header">
                    <h4>{title}</h4>
                    <button type="button" onClick={safeClose}>&times;</button>
                </div>
                {children ? (
                    <div className="bottom-sheet-body">
                        {children}
                    </div>
                ) : options && options.length > 0 ? (
                    <ul>
                        {options.map(option => (
                            <li key={option.value} onClick={() => handleChange(option.value)} style={option.style}>
                                {option.label}
                                {selectedValue === option.value && <ReactSVG src="/assets/images/icon-check.svg" />}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
        </div>
    );
};

export default BottomSheetSelect;
